/* eslint-disable camelcase */
import orderBy from 'lodash/orderBy';
import type { IMarketGroup, IPriceOption } from '@/interface/IOdds';
import { OddsOption } from '@/interface/enum';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { i18n } from '@/libraries/i18n';
import { isNewBetType } from '@/libraries/marketGroupHelper';
import { isFastMarket } from '@/libraries/oddsDisplay';
import type { Price } from '@/types';
import { EnumMarketType } from '@/types';

const optionNameLookup: Partial<Record<EnumMarketType, Record<string, string>>> = {
    Handicap: { h: 'odds_option_home', a: 'odds_option_away' },
    FH_Handicap: { h: 'odds_option_home', a: 'odds_option_away' },
    OverUnder: { h: 'odds_option_over', a: 'odds_option_under' },
    FH_OverUnder: { h: 'odds_option_over', a: 'odds_option_under' },
    OddEven: { h: 'odds_option_odd', a: 'odds_option_even' },
    FH_OddEven: { h: 'odds_option_odd', a: 'odds_option_even' },
    _1X2: { 1: 'odds_option_home', X: 'odds_option_draw', 2: 'odds_option_away' },
    FH_1X2: { 1: 'odds_option_home', X: 'odds_option_draw', 2: 'odds_option_away' },
    MoneyLine: { 1: 'odds_option_home', 2: 'odds_option_away' },
    HTFT: {
        HH: 'odds_option_htft_hh',
        HD: 'odds_option_htft_hd',
        HA: 'odds_option_htft_ha',
        DH: 'odds_option_htft_dh',
        DD: 'odds_option_htft_dd',
        DA: 'odds_option_htft_da',
        AH: 'odds_option_htft_ah',
        AD: 'odds_option_htft_ad',
        AA: 'odds_option_htft_aa',
    },
    TotalGoal: {
        '0-1': 'odds_option_tg_0_1',
        '2-3': 'odds_option_tg_2_3',
        '4-6': 'odds_option_tg_4_6',
        '7-OVER': 'odds_option_tg_7_up',
    },
    DoubleChance: {
        'X2': 'odds_option_dc_2x',
        '12': 'odds_option_dc_12',
        '1X': 'odds_option_dc_1x',
    },
    FGLG: {
        HF: 'odds_option_fglg_hf',
        HL: 'odds_option_fglg_hl',
        AF: 'odds_option_fglg_af',
        AL: 'odds_option_fglg_al',
        NG: 'odds_option_fglg_ng',
    },
};

const optionNameShortLookup: Partial<Record<EnumMarketType, Record<string, string>>> = {
    Handicap: { h: 'odds_option_home_short', a: 'odds_option_away_short' },
    FH_Handicap: { h: 'odds_option_home_short', a: 'odds_option_away_short' },
    OverUnder: { h: 'odds_option_over_short', a: 'odds_option_under_short' },
    FH_OverUnder: { h: 'odds_option_over_short', a: 'odds_option_under_short' },
    OddEven: { h: 'odds_option_odd_short', a: 'odds_option_even_short' },
    FH_OddEven: { h: 'odds_option_odd_short', a: 'odds_option_even_short' },
    _1X2: { 1: 'odds_option_home_short', X: 'odds_option_draw_short', 2: 'odds_option_away_short' },
    FH_1X2: { 1: 'odds_option_home_short', X: 'odds_option_draw_short', 2: 'odds_option_away_short' },
    MoneyLine: { 1: 'odds_option_home_short', 2: 'odds_option_away_short' },
    HTFT: {
        HH: 'odds_option_htft_hh',
        HD: 'odds_option_htft_hd',
        HA: 'odds_option_htft_ha',
        DH: 'odds_option_htft_dh',
        DD: 'odds_option_htft_dd',
        DA: 'odds_option_htft_da',
        AH: 'odds_option_htft_ah',
        AD: 'odds_option_htft_ad',
        AA: 'odds_option_htft_aa',
    },
    TotalGoal: {
        '0-1': 'odds_option_tg_0_1',
        '2-3': 'odds_option_tg_2_3',
        '4-6': 'odds_option_tg_4_6',
        '7-OVER': 'odds_option_tg_7_up',
    },
    DoubleChance: {
        'X2': 'odds_option_dc_2x',
        '12': 'odds_option_dc_12',
        '1X': 'odds_option_dc_1x',
    },
    FGLG: {
        HF: 'odds_option_fglg_hf',
        HL: 'odds_option_fglg_hl',
        AF: 'odds_option_fglg_af',
        AL: 'odds_option_fglg_al',
        NG: 'odds_option_fglg_ng',
    },
};

export function getPriceOptions(
    prices: Pick<Price, 'price' | 'option'>[],
    marketType: EnumMarketType,
    marketGroup: IMarketGroup,
    isLive: boolean,
    liveHomeScore: number,
    liveAwayScore: number,
) {
    const result = prices.map(priceOption => ({
        price: priceOption.price,
        option: priceOption.option,
        oddsOption: OddsOption.fromOddsApi(marketType, priceOption.option),
        optionName: getOptionName(marketType, priceOption.option, marketGroup, isLive, liveHomeScore, liveAwayScore, false),
        optionNameShort: getOptionName(marketType, priceOption.option, marketGroup, isLive, liveHomeScore, liveAwayScore, true),
    }));

    return sortByMarketType(result, marketType);
}

const useTemplateMarketTypes: EnumMarketType[] = [
    EnumMarketType.Handicap,
    EnumMarketType.FH_Handicap,
    EnumMarketType.OverUnder,
    EnumMarketType.FH_OverUnder,
    EnumMarketType._1X2,
    EnumMarketType.FH_1X2,
    EnumMarketType.MoneyLine,
];

function getOptionName(
    marketType: EnumMarketType,
    option: string,
    marketGroup: IMarketGroup,
    isLive: boolean,
    liveHomeScore: number,
    liveAwayScore: number,
    isShort: boolean,
) {
    if (isFastMarket(marketGroup.id)) {
        return getFastMarketOptionName()[option];
    }

    if (isNewBetType(marketGroup.id) && useTemplateMarketTypes.includes(marketType) && isExtensionTemplate(marketGroup.homeTemplate)) {
        return getNewBetTypeTemplateOption(option, marketGroup);
    }

    switch (marketType) {
        case EnumMarketType.LiveScore:
        case EnumMarketType.FH_LiveScore:
            return getAsian1X2OptionName(option, marketType, liveHomeScore, liveAwayScore);
        case EnumMarketType.CorrectScore:
        case EnumMarketType.FH_CorrectScore:
            return getCorrectScoreOptionName(option, isLive, liveHomeScore, liveAwayScore);
        case EnumMarketType.SH_CorrectScore:
            return getSecondHalfCorrectScoreOptionName(option);
        default:
            return getOptionNameByLookup(isShort, marketType, option);
    }
}

function getOptionNameByLookup(isShort: boolean, marketType: EnumMarketType, option: string) {
    const optionName = isShort ? optionNameShortLookup[marketType]?.[option] : optionNameLookup[marketType]?.[option];
    return optionName ?? '';
}

export function isExtensionTemplate(template: string) {
    return template.match(/{(homeext|awayext)}/ig) !== null;
}

function replaceTemplate(marketGroup: IMarketGroup, template: string) {
    const homeExtension = getTranslated(marketGroup.homeExtension);
    const awayExtension = getTranslated(marketGroup.awayExtension);
    return template
        .replace(/_/g, '')
        .replace(/{home}/g, i18n.t('odds_option_home'))
        .replace(/{away}/g, i18n.t('odds_option_away'))
        .replace(/{homeext}/ig, homeExtension)
        .replace(/{awayext}/ig, awayExtension);
}

function getNewBetTypeTemplateOption(option: string, marketGroup: IMarketGroup) {
    const h = replaceTemplate(marketGroup, marketGroup.homeTemplate);
    const a = replaceTemplate(marketGroup, marketGroup.awayTemplate);

    switch (option) {
        case 'h':
        case '1':
            return h;
        case 'a':
        case '2':
            return a;
        case 'X':
            return i18n.t('odds_option_draw');
        default:
            return '';
    }
}

function getCorrectScoreOptionName(
    option: string,
    isLive: boolean,
    liveHomeScore: number,
    liveAwayScore: number,
) {
    if (option === 'O') {
        return 'odds_option_cs_others';
    }

    let homeScore = parseInt((option.split(':')[0]));
    let awayScore = parseInt((option.split(':')[1]));
    if (isLive) {
        homeScore += liveHomeScore;
        awayScore += liveAwayScore;
    }

    return `odds_option_cs_score;${homeScore};${awayScore}`;
}

function getSecondHalfCorrectScoreOptionName(option: string) {
    if (option === 'O') {
        return 'odds_option_cs_others';
    }

    const homeScore = parseInt((option.split(':')[0]));
    const awayScore = parseInt((option.split(':')[1]));

    return `odds_option_cs_score;${homeScore};${awayScore}`;
}

function getAsian1X2OptionName(
    option: string,
    marketType: EnumMarketType,
    liveHomeScore: number,
    liveAwayScore: number,
) {
    const isHalfTime = marketType === EnumMarketType.FH_LiveScore;
    const goals = Math.abs(liveHomeScore - liveAwayScore);

    if (liveHomeScore === liveAwayScore) {
        switch (option) {
            case '1':
            case 'LS_1':
                return 'odds_option_a1x2_teamWins;home';
            case 'x':
            case 'LS_X':
                return `odds_option_a1x2_draw_${(isHalfTime ? 'fh' : 'ft')};`;
            case '2':
            case 'LS_2':
                return 'odds_option_a1x2_teamWins;away';
            default:
                return option;
        }
    } else if (liveHomeScore > liveAwayScore) {
        switch (option) {
            case '1':
            case 'LS_1':
                return `odds_option_a1x2_teamWinsByMargin;home;${goals + 1}`;
            case 'x':
            case 'LS_X':
                return `odds_option_a1x2_teamWinsByExactly;home;${goals}`;
            case '2':
            case 'LS_2':
                if (goals === 1) {
                    return `odds_option_a1x2_teamWinsOrDraw_${(isHalfTime ? 'fh' : 'ft')};away;${goals}`;
                }

                return `odds_option_a1x2_teamLosesByMargin;away;${goals - 1}`;
            default:
                return option;
        }
    } else {
        switch (option) {
            case '1':
            case 'LS_1':
                if (goals === 1) {
                    return `odds_option_a1x2_teamWinsOrDraw_${(isHalfTime ? 'fh' : 'ft')};home;${goals}`;
                }

                return `odds_option_a1x2_teamLosesByMargin;home;${goals - 1}`;
            case 'x':
            case 'LS_X':
                return `odds_option_a1x2_teamWinsByExactly;away;${goals}`;
            case '2':
            case 'LS_2':
                return `odds_option_a1x2_teamWinsByMargin;away;${goals + 1}`;
            default:
                return option;
        }
    }
}

function sortByMarketType(prices: IPriceOption[], marketType: EnumMarketType) {
    switch (marketType) {
        case EnumMarketType._1X2:
        case EnumMarketType.FH_1X2:
            return orderBy(prices, price => price.option.toLowerCase());
        case EnumMarketType.DoubleChance:
            return prices.reverse();
        default:
            return prices;
    }
}

function getFastMarketOptionName(): Record<string, string> {
    return { h: 'odds_option_goal', a: 'odds_option_nogoal' };
}
